import { ReactNode, useEffect } from 'react';
import {
  AgxButton,
  AgxEmptyState,
  AgxRow,
  AgxSaveChangesButton,
  AgxSpacer,
  AgxTypography,
  SAVE_BUTTON_STATES,
  useToast,
} from '@urbanx/agx.ui-components2';
import { FormPrompt } from 'components/FormPrompt';
import LoadingState from 'components/loadingState/LoadingState';
import { MenuTitles, Pages } from 'constants/menu';
import { ErrorModal } from 'helpers/ErrorModal';
import { useSelectedCampaign } from 'hooks';
import { ContractPageScaffold } from '../ContractPageScaffold';
import TerminateContractAction from '../TerminateContractAction';
import { getContractInformationSubPages } from './ContractInformationSubPages';
import type { ContractInformationEditor } from './useContractInformationEditor';

const saveToastId = 'contract-information-save';

interface ContractInformationPageProps<TDraft, TConfirmation> {
  currentPage: Pages;
  editor: ContractInformationEditor<TDraft, TConfirmation>;
  children: ReactNode;
}

export const ContractInformationPage = <TDraft, TConfirmation>({
  currentPage,
  editor,
  children,
}: ContractInformationPageProps<TDraft, TConfirmation>) => {
  const { campaign } = useSelectedCampaign();
  const { pushToast } = useToast();

  useEffect(() => {
    if (!editor.feedback) return;

    pushToast(
      {
        message: editor.feedback.message,
        type: editor.feedback.type,
      },
      saveToastId
    );
  }, [editor.feedback, pushToast]);

  if (editor.isLoading) return <LoadingState />;

  const saveState = (() => {
    if (editor.saving) return SAVE_BUTTON_STATES.SAVING;
    if (editor.dirty) return SAVE_BUTTON_STATES.UNSAVED;
    if (editor.feedback) return SAVE_BUTTON_STATES.SAVED;
    if (editor.loadFailed) return SAVE_BUTTON_STATES.ERROR;
    return SAVE_BUTTON_STATES.UNSAVED;
  })();

  return (
    <ContractPageScaffold
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={currentPage}
      subPages={getContractInformationSubPages({ campaignState: campaign?.state })}
      secondaryActions={<TerminateContractAction />}
      beforeContent={<FormPrompt hasUnsavedChanges={editor.dirty} />}
      headerAction={
        <AgxRow gap="spacing-md" fill alignItems="alignCenter" justifyContent="right">
          {editor.synchronizationIssue && (
            <AgxButton
              id="btnRetryTaskSync"
              text="Try again now"
              size="small"
              variant="hollow"
              onClick={() => void editor.retryTaskSynchronization()}
              disabled={editor.checkingTaskSynchronization}
            />
          )}
          <AgxSaveChangesButton
            state={saveState}
            id="btnSaveChanges"
            text="Save Changes"
            size="small"
            variant="primary"
            onClick={() => void editor.save()}
            disabled={!editor.dirty || editor.saving || editor.checkingTaskSynchronization}
          />
        </AgxRow>
      }
    >
      {editor.loadFailed ? (
        <AgxEmptyState variant="minimal" label="Unable to load contract information." />
      ) : (
        <>
          {editor.synchronizationIssue && (
            <AgxSpacer
              id="contract-information-task-sync-warning"
              direction="vertical"
              gap="spacing-xs"
              role="alert"
            >
              <AgxTypography
                id="contract-information-task-sync-warning-message"
                type="body_ui_strong"
                size="small"
                color="var(--text-warning)"
              >
                {editor.synchronizationIssue.message}
              </AgxTypography>
              {editor.synchronizationIssue.details.length > 0 && (
                <ul>
                  {editor.synchronizationIssue.details.map((detail, index) => (
                    <li key={`${detail}-${index}`}>
                      <AgxTypography
                        id={`contract-information-task-sync-warning-detail-${index}`}
                        type="body_ui"
                        size="small"
                      >
                        {detail}
                      </AgxTypography>
                    </li>
                  ))}
                </ul>
              )}
            </AgxSpacer>
          )}
          {children}
        </>
      )}
      {editor.errors.length > 0 && (
        <ErrorModal
          errorList={[...editor.errors]}
          setShowModal={(showModal) => {
            if (!showModal) editor.dismissErrors();
          }}
        />
      )}
    </ContractPageScaffold>
  );
};
